import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { fetchAwardNoticeDetail } from '../../services/awardNoticeApi';
import SeoPageComponent from '../../components/SeoPageComponent/SeoPageComponent';
import Breadcrumbs from '../../components/Breadcrumbs';
import './AwardNoticeDetails.css';
import Utility from '../../utils/Utility';
import ProjectBadge from '../../components/ProjectBadge/ProjectBadge';
import SaveProcedureButton from '../../components/SaveProcedureButton/SaveProcedureButton';
import ProcedurePaywall from '../../components/ProcedurePaywall/ProcedurePaywall';
import { slugify } from '../../utils/slugify';
import TruncatedText from '../../components/TruncatedText/TruncatedText';

/**
 * AwardNoticeDetails - Componentă unificată pentru toate tipurile de anunțuri de atribuire
 * Suportă: CAN, RFQA, SCNA, PCA, RDC, RFDA
 */
const AwardNoticeDetails = () => {
  const { noticeNo: id } = useParams(); // URL param now holds mongoId
  const [details, setDetails] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);

  // Scroll to top on mount - fixează problema cu încărcarea de jos
  useEffect(() => {
    window.scrollTo(0, 0);
    if (typeof window !== 'undefined') window.prerenderReady = false;
  }, [id]);

  const mapDetails = (data, noticeNoValue) => {
    const generalInfo = data?.generalInfo || {};
    const canForView = data?.canForView || {};
    const isUtilityContract = generalInfo?.isUtilityContract || false;

    // Select the correct structure based on contract type
    const caNoticeEdit = isUtilityContract
      ? generalInfo?.caNoticeEdit_New_U
      : generalInfo?.caNoticeEdit_New;

    const publicationDetails = caNoticeEdit?.publicationDetailsModel || {};
    const section0 = caNoticeEdit?.section0_New || {};

    // Section 1 structures differ for utility contracts
    const section1 = isUtilityContract
      ? caNoticeEdit?.section1_New_U
      : caNoticeEdit?.section1_New;
    const section1_1 = section1?.section1_1 || {};
    const caAddress = section1_1?.caAddress || {};

    // Section 2 structures differ for utility contracts
    const section2 = isUtilityContract
      ? caNoticeEdit?.section2_New_U
      : caNoticeEdit?.section2_New;
    const section2_1 = isUtilityContract
      ? section2?.section2_1_New_U
      : section2?.section2_1_New;
    const mainCpv = section2_1?.mainCPVCode || {};

    const noticeContracts = data?.noticeContracts?.items || [];
    const procedureType =
      generalInfo?.procedureType || section2_1?.sysProcedureType || {};

    return {
      noticeNo: noticeNoValue,
      caNoticeID: canForView?.caNoticeID || generalInfo?.caNoticeID,
      title: generalInfo?.title || section2_1?.contractTitle,
      contractingAuthorityName: caAddress?.officialName,
      contractingAuthorityAddress: caAddress?.address,
      contractingAuthorityCity: caAddress?.city,
      contractValue: section2_1?.totalAcquisitionValue,
      contractCurrency:
        section2_1?.currency?.text || section2_1?.currency?.localeKey,
      sysNoticeState: generalInfo?.sysNoticeState || canForView?.sysNoticeState,
      procedureType: procedureType,
      publicationDate:
        generalInfo?.publicationDate || publicationDetails?.publicationDate,
      cpvCodeAndName: mainCpv?.text || mainCpv?.localeKey,
      canForView,
      generalInfo,
      noticeContracts,
      isUtilityContract,
    };
  };

  // Fetch details
  useEffect(() => {
    if (!id) return;

    const loadDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchAwardNoticeDetail(id);

        if (!data) {
          setNotFound(true);
          return;
        }

        // Extract real SEAP notice code from response; null for free users (partial response)
        const realNoticeNo = data?.canForView?.noticeNumber
          || data?.generalInfo?.noticeNumber
          || data?.extension?.AuctionId
          || data?.extension?.auctionId
          || null;
        setDetails(mapDetails(data, realNoticeNo));

        // Use contracts included in detail response (if any)
        setContracts(data?.noticeContracts?.items || []);
      } catch (err) {
        console.error('Error loading notice details:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadDetails();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">Se încarcă detaliile...</p>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-red-500">Anunțul nu a fost găsit</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-red-500">Eroare: {error}</p>
      </div>
    );
  }

  // Real SEAP notice code (only populated for subscribers — free users have it hidden)
  const noticeNo = details?.noticeNo || null;
  const pageHeadline = details?.title || 'Detalii anunț de atribuire';

  const breadcrumbLinks = [
    { href: '/', label: 'Home' },
    { href: '/anunturi-atribuire/', label: 'Anunturi Atribuire' },
    { label: pageHeadline },
  ];

  const defaultTitle = 'Licitatii-Constructii – Anunțuri de atribuire';
  const defaultDesc = 'Informații despre anunțurile de atribuire publicate în SEAP.';
  const pageTitle = details?.title ? `${details.title} – Licitatii-Constructii` : defaultTitle;
  const pageDescription = details?.title
    ? `Informații despre anunțul de atribuire ${details.title}${details?.contractingAuthority ? `, publicat de ${details.contractingAuthority}` : ''}.`
    : defaultDesc;

  const renderValue = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'boolean') return value ? 'Da' : 'Nu';
    if (Array.isArray(value)) return value.length ? value.join(', ') : '—';
    if (typeof value === 'object') return JSON.stringify(value);
    return value;
  };

  const isValidDateValue = (value) => {
    if (!value) return false;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return false;
    return date.getFullYear() > 1900;
  };

  const renderDate = (value) =>
    isValidDateValue(value) ? Utility.FormatDateLight(value) : '—';

  const generalInfo = details?.generalInfo || {};
  const canForView = details?.canForView || {};
  const isUtilityContract = details?.isUtilityContract || false;

  // Select the correct structure based on contract type
  const caNoticeEdit = isUtilityContract
    ? generalInfo?.caNoticeEdit_New_U
    : generalInfo?.caNoticeEdit_New;

  const publicationDetails = caNoticeEdit?.publicationDetailsModel || {};
  const section0 = caNoticeEdit?.section0_New || {};

  // Section 1 structures
  const section1 = isUtilityContract
    ? caNoticeEdit?.section1_New_U
    : caNoticeEdit?.section1_New;
  const section1_1 = section1?.section1_1 || {};
  const section1_2 = isUtilityContract
    ? section1?.section1_2_U
    : section1?.section1_2_New || {};
  const section1_4 = isUtilityContract
    ? section1?.section1_4_U
    : section1?.section1_4_New || {};
  const section1_5 = isUtilityContract
    ? section1?.section1_6_U
    : section1?.section1_5 || {};
  const caAddress = section1_1?.caAddress || {};

  // Section 2 structures
  const section2 = isUtilityContract
    ? caNoticeEdit?.section2_New_U
    : caNoticeEdit?.section2_New || {};
  const section2_1 = isUtilityContract
    ? section2?.section2_1_New_U
    : section2?.section2_1_New || {};
  const section2_2 = isUtilityContract
    ? section2?.section2_2_New_U
    : section2?.section2_2_New || {};

  // Section 4 and 6 remain the same
  const section4 = caNoticeEdit?.section4_New || {};
  const section4_1 = section4?.section4_1_New || {};
  const section4_2 = section4?.section4_2_New || {};
  const section6 = caNoticeEdit?.section6_New || {};
  const section6_3 = section6?.section6_3_New || {};
  const section6_4_1 = section6?.section6_4_1_New || {};
  const section6_4_3 = section6?.section6_4_3_New || {};
  const section6_4_4 = section6?.section6_4_4_New || {};
  const section6_5 = section6?.section6_5_New || {};

  const noticeContracts = details?.noticeContracts || contracts || [];
  const primaryContract = noticeContracts?.[0] || null;
  const primaryWinner =
    primaryContract?.winner || primaryContract?.winners?.[0] || null;

  const ogImage = details?.imageUrl || `${import.meta.env.VITE_DOMAIN}/og-default.png`;
  const canonical = `${import.meta.env.VITE_DOMAIN}/anunturi-atribuire/${slugify(details?.title)}/${id}`;

  return (
    <>
      <SeoPageComponent
        pageTitle={pageTitle}
        pageDescription={pageDescription}
        pageUrl={canonical}
        logoUrl={ogImage}
        keywords={`anunturi atribuire, ${details?.cpvCodeAndName || ''}, ${details?.title || ''}`}
        canonicalUrl={canonical}
        robotsContent="index,follow"
      />
      <section className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-green-100 py-12">
        <div className="container mx-auto px-4 max-w-7xl">
          <Breadcrumbs breadcrumbLinks={breadcrumbLinks} />

          {/* Main Details */}
          <div className="bg-white rounded-2xl shadow-2xl p-8 border-t-8 border-indigo-500 mb-10">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                {!details?.isPartial && (
                  <span className="inline-block bg-indigo-100 text-indigo-700 px-4 py-2 rounded-full font-semibold text-sm">
                    {noticeNo}
                  </span>
                )}
                <SaveProcedureButton noticeNo={noticeNo} />
              </div>
              <h1 className="text-4xl font-extrabold text-gray-800 mb-4">
                {details?.title}
              </h1>
              {details?.cpvCodeAndName && (
                <p className="text-gray-600">
                  <strong>CPV:</strong> {details.cpvCodeAndName}
                </p>
              )}
            </div>

            {/* Key Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Contracting Authority */}
              <div className="p-4 bg-indigo-50 rounded-lg">
                <div className="text-center">
                  <div className="text-2xl mb-2">🏛️</div>
                  <h3 className="text-lg font-bold text-indigo-700 mb-2">
                    Autoritate Contractantă
                  </h3>
                  <p className="text-gray-700 font-semibold">
                    {details?.contractingAuthorityName}
                  </p>
                </div>
                {details?.contractingAuthorityAddress && (
                  <p className="text-sm text-gray-600 mt-2 text-center">
                    {details.contractingAuthorityAddress}
                  </p>
                )}
                {details?.contractingAuthorityCity && (
                  <p className="text-sm text-gray-600 text-center">
                    {details.contractingAuthorityCity}
                  </p>
                )}
              </div>

              {/* Contract Value */}
              <div className="p-4 bg-green-50 rounded-lg">
                <div className="text-center">
                  <div className="text-2xl mb-2">💰</div>
                  <h3 className="text-lg font-bold text-green-700 mb-2">
                    Valoare Contract
                  </h3>
                  <p className="text-gray-700 font-semibold">
                    {Utility.ConvertEstimatedValue(details?.contractValue || 0)}
                  </p>
                  <p className="text-sm text-gray-600">
                    {details?.contractCurrency}
                  </p>
                </div>
              </div>

              {/* Notice State */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <div className="text-center">
                  <div className="text-2xl mb-2">📢</div>
                  <h3 className="text-lg font-bold text-blue-700 mb-2">
                    Starea Anunțului
                  </h3>
                  <p className="text-gray-700">
                    {details?.sysNoticeState?.text || 'Necunoscut'}
                  </p>
                </div>
              </div>

              {/* Procedure Type */}
              <div className="p-4 bg-purple-50 rounded-lg">
                <div className="text-center">
                  <div className="text-2xl mb-2">📄</div>
                  <h3 className="text-lg font-bold text-purple-700 mb-2">
                    Tip Procedură
                  </h3>
                  <p className="text-gray-700">
                    {details?.procedureType?.text || 'Necunoscut'}
                  </p>
                </div>
              </div>

              {/* Publication Date */}
              <div className="p-4 bg-yellow-50 rounded-lg">
                <div className="text-center">
                  <div className="text-2xl mb-2">🗓️</div>
                  <h3 className="text-lg font-bold text-yellow-700 mb-2">
                    Data Publicării
                  </h3>
                  <p className="text-gray-700">
                    {renderDate(details?.publicationDate)}
                  </p>
                </div>
              </div>

              {/* Legislation */}
              <div className="p-4 bg-orange-50 rounded-lg">
                <div className="text-center">
                  <div className="text-2xl mb-2">⚖️</div>
                  <h3 className="text-lg font-bold text-orange-700 mb-2">
                    Legislație
                  </h3>
                  <p className="text-gray-700">
                    {generalInfo?.legislationType?.text || 'Necunoscut'}
                  </p>
                </div>
              </div>

              {/* Original Notice */}
              <div className="p-4 bg-teal-50 rounded-lg">
                <div className="text-center">
                  <div className="text-2xl mb-2">📋</div>
                  <h3 className="text-lg font-bold text-teal-700 mb-2">
                    Anunț Original
                  </h3>
                  <p className="text-gray-700 font-semibold">
                    {publicationDetails?.noticeNo || '—'}
                  </p>
                  {generalInfo?.prevPubSysNoticeType?.text && (
                    <p className="text-sm text-gray-600">
                      {generalInfo.prevPubSysNoticeType.text}
                    </p>
                  )}
                </div>
              </div>

              {/* Winner */}
              <div className="p-4 bg-emerald-50 rounded-lg">
                <div className="text-center">
                  <div className="text-2xl mb-2">🏆</div>
                  <h3 className="text-lg font-bold text-emerald-700 mb-2">
                    Câștigător
                  </h3>
                  <p className="text-gray-700 font-semibold">
                    {primaryWinner?.name ||
                      primaryContract?.winnerCaption ||
                      '—'}
                  </p>
                  <p className="text-sm text-gray-600">
                    {primaryWinner?.fiscalNumber || '—'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Proiect asociat */}
          <div className="mb-10">
            <ProjectBadge auctionId={noticeNo} variant="section" />
          </div>

          {/* Section V - Atribuiri */}
          {noticeContracts.length > 0 && (
            <div className="bg-white rounded-2xl shadow-2xl p-8 border-t-8 border-green-500 mb-10">
              <h2 className="text-3xl font-extrabold text-gray-800 mb-6">
                SECȚIUNEA V: Atribuiri
              </h2>
              <div className="space-y-6">
                {noticeContracts.map((contract, index) => (
                  <div
                    key={index}
                    className="p-6 border border-gray-200 rounded-lg bg-gray-50"
                  >
                    <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                      <div>
                        <h3 className="text-xl font-bold text-indigo-700 mb-1">
                          {contract.contractNo || 'Contract'}
                        </h3>
                        <p className="text-gray-700 mb-1">
                          {contract.contractTitle}
                        </p>
                        <p className="text-sm text-gray-600">
                          Data contract: {renderDate(contract.contractDate)}
                        </p>
                        {(contract.lotsCaption || contract.lotsNoCaption) && (
                          <p className="text-sm text-gray-600">
                            Lot:{' '}
                            {contract.lotsCaption || contract.lotsNoCaption}
                          </p>
                        )}
                      </div>
                      <div className="text-left md:text-right">
                        <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold">
                          {Utility.ConvertEstimatedValue(
                            contract.contractValue,
                          )}{' '}
                          {contract.currency?.text ||
                            contract.currency?.localeKey}
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div className="p-4 bg-white rounded-lg border border-gray-200">
                        <h4 className="font-bold text-gray-800 mb-2">
                          Câștigător
                        </h4>
                        <p className="text-gray-700 font-semibold">
                          {contract.winner?.name ||
                            contract.winnerCaption ||
                            '—'}
                        </p>
                        <p className="text-sm text-gray-600">
                          CIF: {contract.winner?.fiscalNumber || '—'}
                        </p>
                        {(contract.winner?.address?.address ||
                          contract.winner?.address?.city) && (
                          <p className="text-sm text-gray-600 mt-1">
                            {[
                              contract.winner?.address?.address,
                              contract.winner?.address?.city,
                            ]
                              .filter(Boolean)
                              .join(', ')}
                          </p>
                        )}
                      </div>
                      <div className="p-4 bg-white rounded-lg border border-gray-200">
                        <h4 className="font-bold text-gray-800 mb-2">
                          Participanți
                        </h4>
                        {Array.isArray(contract.winners) &&
                        contract.winners.length > 0 ? (
                          <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                            {contract.winners.map((winner, wIndex) => (
                              <li key={wIndex}>
                                {winner?.name}{' '}
                                {winner?.fiscalNumber
                                  ? `(${winner.fiscalNumber})`
                                  : ''}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-sm text-gray-600">
                            {contract.winnerCaption || '—'}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Section I */}
          <div className="bg-white rounded-2xl shadow-2xl p-8 border-t-8 border-indigo-500 mb-10">
            <h2 className="text-3xl font-extrabold text-gray-800 mb-6">
              SECȚIUNEA I: Autoritatea contractantă
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-bold text-gray-700 mb-2">
                  Denumire și adrese
                </h3>
                <p className="text-gray-700 font-semibold">
                  {renderValue(caAddress?.officialName)}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  CIF: {renderValue(caAddress?.nationalIDNumber)}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {renderValue(caAddress?.address)}
                </p>
                <p className="text-sm text-gray-600">
                  {renderValue(caAddress?.city)},{' '}
                  {renderValue(caAddress?.postalCode)}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  Email: {renderValue(caAddress?.email)}
                </p>
                <p className="text-sm text-gray-600">
                  Tel: {renderValue(caAddress?.phone)}
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-bold text-gray-700 mb-2">
                  Clasificare
                </h3>
                <p className="text-sm text-gray-600">
                  <strong>Tip autoritate:</strong>{' '}
                  {renderValue(section1_4?.caType?.text)}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  <strong>Activitate principală:</strong>{' '}
                  {renderValue(section1_5?.mainActivity?.text)}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  <strong>Achiziție comună:</strong>{' '}
                  {renderValue(section1_2?.isCommonProcurement)}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  <strong>Organism central:</strong>{' '}
                  {renderValue(section1_2?.isAwardedByCentralAcquisitionOrgan)}
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-bold text-gray-700 mb-2">
                  Contact
                </h3>
                <p className="text-sm text-gray-600">
                  <strong>Contact persoană:</strong>{' '}
                  {renderValue(caAddress?.contactPerson)}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  <strong>Atenție la:</strong>{' '}
                  {renderValue(caAddress?.attentionTo)}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  <strong>Website:</strong>{' '}
                  {renderValue(caAddress?.caMainAddressUrl)}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  <strong>Profil cumpărător:</strong>{' '}
                  {renderValue(caAddress?.buyerProfileUrl)}
                </p>
              </div>
            </div>
          </div>

          {/* Section II */}
          <div className="bg-white rounded-2xl shadow-2xl p-8 border-t-8 border-green-500 mb-10">
            <h2 className="text-3xl font-extrabold text-gray-800 mb-6">
              SECȚIUNEA II: Obiect
            </h2>
            <div className="p-4 bg-gray-50 rounded-lg mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">
                    <strong>Titlu:</strong>{' '}
                    {renderValue(section2_1?.contractTitle)}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    <strong>Nr. referință:</strong>{' '}
                    {renderValue(section2_1?.referenceNumber)}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    <strong>Tip contract:</strong>{' '}
                    {renderValue(section2_1?.sysAcquisitionContractType?.text)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">
                    <strong>Valoare totală:</strong>{' '}
                    {Utility.ConvertEstimatedValue(
                      section2_1?.totalAcquisitionValue || 0,
                    )}{' '}
                    {renderValue(
                      section2_1?.currency?.text ||
                        section2_1?.currency?.localeKey,
                    )}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    <strong>Împărțit în loturi:</strong>{' '}
                    {renderValue(section2_1?.hasLots)}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    <strong>CPV:</strong>{' '}
                    {renderValue(
                      section2_1?.mainCPVCode?.text ||
                        section2_1?.mainCPVCode?.localeKey,
                    )}
                  </p>
                </div>
              </div>
            </div>

            {Array.isArray(section2_2?.descriptionList) &&
              section2_2.descriptionList.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-gray-800">
                    Loturi/Obiecte
                  </h3>
                  {section2_2.descriptionList.map((lot, index) => (
                    <div
                      key={index}
                      className="p-4 border border-gray-200 rounded-lg bg-gray-50"
                    >
                      <h4 className="font-bold text-gray-800 mb-2">
                        {lot?.contractTitle ||
                          `Lot ${lot?.lotNumber || index + 1}`}
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="text-sm text-gray-700">
                          <p>
                            <strong>CPV:</strong>{' '}
                            {renderValue(
                              lot?.mainCPVCodes?.text ||
                                lot?.mainCPVCodes?.localeKey,
                            )}
                          </p>
                          <p className="mt-1">
                            <strong>Valoare:</strong>{' '}
                            {Utility.ConvertEstimatedValue(
                              lot?.estimatedValue || 0,
                            )}
                          </p>
                          <p className="mt-1">
                            <strong>Stare:</strong> {renderValue(lot?.lotInfo)}
                          </p>
                        </div>
                        <div className="text-sm text-gray-700">
                          <p>
                            <strong>Loc:</strong>{' '}
                            {renderValue(lot?.mainLocation)}
                          </p>
                          <p className="mt-1">
                            <strong>Durată:</strong> {lot?.monthDuration || 0}{' '}
                            luni{' '}
                            {lot?.dayDuration
                              ? `și ${lot.dayDuration} zile`
                              : ''}
                          </p>
                        </div>
                      </div>
                      {Array.isArray(lot?.noticeAwardCriteriaList) &&
                        lot.noticeAwardCriteriaList.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-300">
                            <p className="text-sm font-bold text-gray-700 mb-2">
                              Criterii de atribuire:
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {lot.noticeAwardCriteriaList.map((c, cIdx) => (
                                <p key={cIdx} className="text-sm text-gray-700">
                                  • {c?.noticeAwardCriteriaName} -{' '}
                                  {c?.noticeAwardCriteriaWeight || 0}%
                                </p>
                              ))}
                            </div>
                          </div>
                        )}
                    </div>
                  ))}
                </div>
              )}
          </div>

          {/* Section IV */}
          <div className="bg-white rounded-2xl shadow-2xl p-8 border-t-8 border-blue-500 mb-10">
            <h2 className="text-3xl font-extrabold text-gray-800 mb-6">
              SECȚIUNEA IV: Procedura
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-bold text-gray-700 mb-2">
                  Descriere
                </h3>
                <p className="text-sm text-gray-600">
                  <strong>Tip procedură:</strong>{' '}
                  {renderValue(section4_1?.sysProcedureType?.text)}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  <strong>Acord-cadru:</strong>{' '}
                  {renderValue(section4_1?.sysContractAssigmentType?.text)}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  <strong>Licitație electronică:</strong>{' '}
                  {renderValue(section4_1?.hasElectronicAuction)}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  <strong>Incidența AAP:</strong>{' '}
                  {renderValue(section4_1?.hasGpaAgreement)}
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-bold text-gray-700 mb-2">
                  Info administrative
                </h3>
                <p className="text-sm text-gray-600">
                  <strong>Publicare anterioară:</strong>{' '}
                  {renderValue(section4_2?.previousPublicationNoticeNumber)}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  <strong>Publicație JOUE:</strong>{' '}
                  {renderValue(publicationDetails?.jouePublicationNumber)}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  <strong>Anunț JOUE:</strong>{' '}
                  {renderValue(generalInfo?.tedNoticeNo)}
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-bold text-gray-700 mb-2">
                  Condiții speciale
                </h3>
                <p className="text-sm text-gray-600">
                  <strong>Accord-cadru:</strong>{' '}
                  {caNoticeEdit?.section0_New?.sentToJOUE ? 'Da' : 'Nu'}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  <strong>Procedură online:</strong>{' '}
                  {generalInfo?.isOnlineProcedure ? 'Da' : 'Nu'}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  <strong>Contract-cadru:</strong>{' '}
                  {section4_1?.sysContractAssigmentType?.text || 'Nu'}
                </p>
              </div>
            </div>
          </div>

          {/* Section VI */}
          <div className="bg-white rounded-2xl shadow-2xl p-8 border-t-8 border-purple-500 mb-10">
            <h2 className="text-3xl font-extrabold text-gray-800 mb-6">
              SECȚIUNEA VI: Informații suplimentare
            </h2>
            {section6_3?.supplementaryInformation && (
              <div className="p-4 bg-gray-50 rounded-lg mb-6">
                <h3 className="text-lg font-bold text-gray-700 mb-2">
                  Informații suplimentare
                </h3>
                <TruncatedText text={section6_3.supplementaryInformation} />
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-bold text-gray-700 mb-2">
                  Organism contestații
                </h3>
                <p className="text-sm text-gray-600">
                  <strong>
                    {renderValue(
                      section6_4_1?.disputeHandleOrganization?.officialName,
                    )}
                  </strong>
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {renderValue(
                    section6_4_1?.disputeHandleOrganization?.address,
                  )}
                </p>
                <p className="text-sm text-gray-600">
                  {renderValue(section6_4_1?.disputeHandleOrganization?.email)}
                </p>
                <p className="text-sm text-gray-600">
                  {renderValue(section6_4_1?.disputeHandleOrganization?.phone)}
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-bold text-gray-700 mb-2">
                  Procedură contestare
                </h3>
                <p className="text-sm text-gray-600">
                  {renderValue(section6_4_3?.disputeProcedure)}
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-bold text-gray-700 mb-2">
                  Serviciu informații
                </h3>
                <p className="text-sm text-gray-600">
                  <strong>
                    {renderValue(
                      section6_4_4?.disputeInformationService?.officialName,
                    )}
                  </strong>
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {renderValue(
                    section6_4_4?.disputeInformationService?.address,
                  )}
                </p>
                <p className="text-sm text-gray-600">
                  {renderValue(section6_4_4?.disputeInformationService?.email)}
                </p>
              </div>
            </div>
            {isValidDateValue(section6_5?.noticeSendDate) && (
              <div className="p-4 bg-gray-50 rounded-lg mt-4">
                <p className="text-sm text-gray-600">
                  <strong>Data expedierii anunțului:</strong>{' '}
                  {renderDate(section6_5?.noticeSendDate)}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Paywall pentru utilizatorii fără abonament */}
      {details?.isPartial && <ProcedurePaywall />}
    </>
  );
};

export default AwardNoticeDetails;
